import type { ODataResponse, OrganizationDto, UserProfileWorkerDto, TemplateDto } from "./fbo-api.types"
import type { GetToken } from "./ats-sync-api.client"
import { env } from "@/lib/env"
import { TEMPLATE_STATUS } from "@/components/ats-fbo/constants"

const TOP = 50

function escapeODataStringLiteral(value: string): string {
  return value.replace(/'/g, "''")
}

export type SupplierRoleProperty =
  | "IsOrganizationIndependentContractorRole"
  | "IsOrganizationLimitedLiabilityCompanyRole"
  | "IsOrganizationSubVendorRole"

async function get<T>(path: string, getToken: GetToken): Promise<T> {
  const token = await getToken()
  const response = await fetch(`${env.fboApiUrl}/${path}`, {
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
    },
  })
  if (!response.ok) {
    throw new Error(`FBO API ${response.status}: ${response.statusText}`)
  }
  return response.json()
}

function buildOrgEndpoint(role: "InternalRole" | "ClientRole", search: string, selectedId?: number | null): string {
  // The client picker uses a dedicated route that evaluates the
  // `allowInactiveClientOrgOnOnboardingForm` feature flag server-side: with the flag off it behaves exactly
  // like the ...ActiveClientRole route, and with it on it also offers orgs whose own status is not
  // Active/PendingChange (an ATS auto-created client org is written as a draft, so it was invisible here).
  // Both branches still require an active Client role. Internal-company search is unchanged.
  const base =
    role === "ClientRole"
      ? "api/org/getListOrganizationsForOnboardingFormClientPicker"
      : `api/org/getListOrganizationsOriginalAndStatusIsAtiveOrPendingChangeInActive${role}`
  const params = new URLSearchParams()
  params.set("$select", "Id,DisplayName,Code")
  params.set("$top", String(TOP))
  const filters: string[] = []
  if (search) {
    const lower = escapeODataStringLiteral(search.toLowerCase())
    filters.push(`substringof('${lower}', tolower(DisplayName)) eq true`)
  }
  if (selectedId != null) {
    // Wrap search filter with OR so the selected item is always returned
    const searchPart = filters.length > 0 ? `(${filters.join(" and ")})` : null
    const idPart = `Id eq ${selectedId}`
    params.set("$filter", searchPart ? `${searchPart} or ${idPart}` : idPart)
  } else if (filters.length > 0) {
    params.set("$filter", filters.join(" and "))
  }
  return `${base}?${params.toString()}`
}

function buildWorkerProfileQueryParams(scopeFilter: string): URLSearchParams {
  const params = new URLSearchParams()
  params.set("$expand", "Contact")
  params.set(
    "$select",
    // Contact/CultureId surfaces the contact-level preferred language (88 = French,
    // 48 = English; anything else is treated as English-default by the consumer).
    "Id,ProfileStatusId,ContactId,ProfileTypeId,OrganizationId,PrimaryEmail,T4TypeId,Contact/FullName,Contact/UserStatusId,Contact/CultureId",
  )
  params.set("$top", String(TOP))
  params.set("$filter", scopeFilter)
  return params
}

function buildWorkerProfileByContactEndpoint(contactId: number): string {
  const base = `api/UserProfile/getListUserProfileWorker`
  const scopeFilter = `ContactId eq ${contactId}`
  const params = buildWorkerProfileQueryParams(scopeFilter)
  return `${base}?${params.toString()}`
}

function buildWorkerProfileByEmailEndpoint(email: string): string {
  const base = `api/UserProfile/getListUserProfileWorker`
  const scopeFilter = `tolower(PrimaryEmail) eq '${escapeODataStringLiteral(email.toLowerCase())}'`
  const params = buildWorkerProfileQueryParams(scopeFilter)
  return `${base}?${params.toString()}`
}

function buildWorkerProfileByIdEndpoint(profileId: number): string {
  const base = `api/UserProfile/getListUserProfileWorker`
  const params = new URLSearchParams()
  params.set("$expand", "Contact")
  params.set(
    "$select",
    // Contact/CultureId surfaces the contact-level preferred language (88 = French,
    // 48 = English; anything else is treated as English-default by the consumer).
    "Id,ProfileStatusId,ContactId,ProfileTypeId,OrganizationId,PrimaryEmail,T4TypeId,Contact/FullName,Contact/UserStatusId,Contact/CultureId",
  )
  params.set("$top", String(TOP))
  params.set("$filter", `Id eq ${profileId}`)
  return `${base}?${params.toString()}`
}

export const fboApi = {
  async searchInternalOrgs(search: string, getToken: GetToken, selectedId?: number | null): Promise<OrganizationDto[]> {
    const endpoint = buildOrgEndpoint("InternalRole", search, selectedId)
    const data = await get<ODataResponse<OrganizationDto>>(endpoint, getToken)
    return data.Items ?? []
  },

  async searchClientOrgs(search: string, getToken: GetToken, selectedId?: number | null): Promise<OrganizationDto[]> {
    const endpoint = buildOrgEndpoint("ClientRole", search, selectedId)
    const data = await get<ODataResponse<OrganizationDto>>(endpoint, getToken)
    return data.Items ?? []
  },

  // TODO: Remove, future decision to remove this from blocked placements warning in table
  async searchWorkerProfilesByEmail(
    email: string,
    getToken: GetToken,
  ): Promise<UserProfileWorkerDto[]> {
    const endpoint = buildWorkerProfileByEmailEndpoint(email)
    const data = await get<ODataResponse<UserProfileWorkerDto>>(endpoint, getToken)
    return data.Items ?? []
  },

  /** Search worker profiles scoped to a single contact */
  async searchWorkerProfilesByContact(
    contactId: number,
    getToken: GetToken,
  ): Promise<UserProfileWorkerDto[]> {
    const endpoint = buildWorkerProfileByContactEndpoint(contactId)
    const data = await get<ODataResponse<UserProfileWorkerDto>>(endpoint, getToken)
    return data.Items ?? []
  },

  async searchSupplierOrgs(search: string, roleProperty: SupplierRoleProperty, getToken: GetToken, selectedId?: number | null): Promise<OrganizationDto[]> {
    const base = `api/org/getListSupplierOrganizationsOriginal`
    const params = new URLSearchParams()
    params.set("$select", "Id,DisplayName,Code")
    params.set("$top", String(TOP))
    const scopePart = `${roleProperty} eq true`
    let searchPart: string | null = null
    if (search) {
      const lower = escapeODataStringLiteral(search.toLowerCase())
      searchPart = `substringof('${lower}', tolower(DisplayName)) eq true`
    }
    if (selectedId != null) {
      const selectedIdPart = `Id eq ${selectedId}`
      params.set("$filter", searchPart ? `${scopePart} and (${searchPart} or ${selectedIdPart})` : `${scopePart} and (${selectedIdPart})`)
    } else {
      params.set("$filter", searchPart ? `${scopePart} and ${searchPart}` : scopePart)
    }
    const data = await get<ODataResponse<OrganizationDto>>(`${base}?${params.toString()}`, getToken)
    return data.Items ?? []
  },

  /** Fetch a single worker profile by its ID, including Contact for contactId resolution */
  async getWorkerProfileById(profileId: number, getToken: GetToken): Promise<UserProfileWorkerDto | null> {
    const endpoint = buildWorkerProfileByIdEndpoint(profileId)
    const data = await get<ODataResponse<UserProfileWorkerDto>>(endpoint, getToken)
    return data.Items?.[0] ?? null
  },

  /** Fetch all assignment templates (entityTypeId 8 = Assignment) */
  async getTemplates(getToken: GetToken): Promise<TemplateDto[]> {
    const data = await get<ODataResponse<TemplateDto>>(`api/template/getTemplatesByEntityTypeId/8?$filter=StatusId eq ${TEMPLATE_STATUS.Active}`, getToken)
    return data.Items ?? []
  },
}
